"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { FileText, Search, Stethoscope, User, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { algoliaSearchService } from "@/lib/services/algolia-search-service";
import { sanitizeHighlight } from "@/lib/utils/sanitize";

interface SearchResult {
  type: "patient" | "document" | "procedure";
  id: string;
  title: string;
  subtitle: string;
  metadata: Record<string, any>;
  highlight?: Record<string, any>;
  snippet?: Record<string, any>;
}

interface AlgoliaSearchProps {
  onPatientSelect?: (patient: any) => void;
  onDocumentSelect?: (document: any) => void;
  placeholder?: string;
  showFilters?: boolean;
  defaultTab?: string;
}

export function AlgoliaSearch({
  onPatientSelect,
  onDocumentSelect,
  placeholder = "Search patients, procedures, documents, appointments, and more...",
  showFilters = true,
  defaultTab = "all",
}: AlgoliaSearchProps) {
  const [query, setQuery] = useState("");
  const [activeTab, _setActiveTab] = useState(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filters, setFilters] = useState({
    specialty: "",
    status: "",
    documentType: "",
    dateRange: { start: "", end: "" },
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const formatSearchResult = useCallback((item: any, type: string): SearchResult => {
    switch (type) {
      case "patients":
        return {
          type: "patient",
          id: item.objectID,
          title: item.name,
          subtitle: `${item.mrn} • ${item.specialty} • ${item.status}`,
          metadata: item,
          highlight: item._highlightResult,
          snippet: item._snippetResult,
        };
      case "documents":
        return {
          type: "document",
          id: item.objectID,
          title: item.title,
          subtitle: `${item.type} • ${item.patientName} • ${new Date(item.timestamp).toLocaleDateString()}`,
          metadata: item,
          highlight: item._highlightResult,
          snippet: item._snippetResult,
        };
      case "procedures":
        return {
          type: "procedure",
          id: item.objectID,
          title: item.name,
          subtitle: `${item.cptCode} • ${item.patientName} • ${item.date}`,
          metadata: item,
          highlight: item._highlightResult,
        };
      default:
        return {
          type: "patient",
          id: item.objectID,
          title: item.name || item.title,
          subtitle: item.subtitle || "",
          metadata: item,
        };
    }
  }, []);

  const performSearch = useCallback(async () => {
    if (!query.trim()) return;

    setIsLoading(true);
    const searchPromises: Promise<any>[] = [];

    try {
      if (activeTab === "all" || activeTab === "patients") {
        searchPromises.push(
          algoliaSearchService.searchPatients(query, {
            specialty: filters.specialty || undefined,
            status: filters.status || undefined,
            limit: 10,
          })
        );
      }

      if (activeTab === "all" || activeTab === "documents") {
        searchPromises.push(
          algoliaSearchService.searchDocuments(query, {
            documentType: filters.documentType || undefined,
            dateRange: filters.dateRange.start && filters.dateRange.end ? filters.dateRange : undefined,
            limit: 10,
          })
        );
      }

      if (activeTab === "all" || activeTab === "procedures") {
        searchPromises.push(
          algoliaSearchService.searchProcedures(query, {
            specialty: filters.specialty || undefined,
            dateRange: filters.dateRange.start && filters.dateRange.end ? filters.dateRange : undefined,
            limit: 10,
          })
        );
      }

      const searchResults = await Promise.all(searchPromises);
      const combinedResults: SearchResult[] = [];

      searchResults.forEach((result, index) => {
        if (result.success && result.data) {
          const resultType = activeTab === "all" ? ["patients", "documents", "procedures"][index] : activeTab;

          result.data.forEach((item: any) => {
            combinedResults.push(formatSearchResult(item, resultType));
          });
        }
      });

      setResults(combinedResults);
      setTotalResults(searchResults.reduce((total, result) => total + (result.total || 0), 0));
    } catch (_error) {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, activeTab, filters, formatSearchResult]);

  const getPatientSuggestions = useCallback(async () => {
    try {
      const result = await algoliaSearchService.getPatientSuggestions(query, 5);
      if (result.success) {
        setSuggestions(result.suggestions || []);
        setShowSuggestions(true);
      }
    } catch (_error) {}
  }, [query]);

  // Debounced search
  useEffect(() => {
    const searchTimer = setTimeout(() => {
      if (query.length >= 2) {
        performSearch();
        if (activeTab === "patients" || defaultTab === "all") {
          getPatientSuggestions();
        }
      } else {
        setResults([]);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [query, activeTab, defaultTab, getPatientSuggestions, performSearch]);

  // Hide suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleResultClick = (result: SearchResult) => {
    if (result.type === "patient" && onPatientSelect) {
      onPatientSelect(result.metadata);
    } else if (result.type === "document" && onDocumentSelect) {
      onDocumentSelect(result.metadata);
    }
    setQuery("");
    setResults([]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (onPatientSelect) {
      onPatientSelect(suggestion);
    }
    setQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const clearFilters = () => {
    setFilters({
      specialty: "",
      status: "",
      documentType: "",
      dateRange: { start: "", end: "" },
    });
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case "patient":
        return <User className="h-4 w-4" />;
      case "document":
        return <FileText className="h-4 w-4" />;
      case "procedure":
        return <Stethoscope className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  const getResultColor = (type: string) => {
    switch (type) {
      case "patient":
        return "bg-blue-50 border-blue-200 text-blue-700";
      case "document":
        return "bg-green-50 border-green-200 text-green-700";
      case "procedure":
        return "bg-purple-50 border-purple-200 text-purple-700";
      default:
        return "bg-gray-50 border-gray-200 text-gray-700";
    }
  };

  return (
    <div className="relative mx-auto w-full max-w-4xl">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          className="py-3 pl-10 pr-4 text-lg"
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-2 top-1/2 -translate-y-1/2 transform"
            onClick={() => {
              setQuery("");
              setResults([]);
              setSuggestions([]);
              setShowSuggestions(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Autocomplete Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {suggestions.map((suggestion, _index) => (
            <div
              key={suggestion.id}
              className="cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-blue-500" />
                <div>
                  <div className="font-medium">{suggestion.name}</div>
                  <div className="text-sm text-gray-500">
                    {suggestion.mrn} • {suggestion.specialty}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search Filters */}
      {showFilters && (
        <div className="mt-4 rounded-lg bg-gray-50 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Select
              value={filters.specialty || "all"}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, specialty: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Specialty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Specialties</SelectItem>
                <SelectItem value="Oncology">Oncology</SelectItem>
                <SelectItem value="Gastroenterology">Gastroenterology</SelectItem>
                <SelectItem value="Psychiatry">Psychiatry</SelectItem>
                <SelectItem value="Cardiology">Cardiology</SelectItem>
                <SelectItem value="Endocrinology">Endocrinology</SelectItem>
                <SelectItem value="Pulmonology">Pulmonology</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
                <SelectItem value="Remission">Remission</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.documentType || "all"}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, documentType: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Document Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Documents</SelectItem>
                <SelectItem value="Radiology">Radiology</SelectItem>
                <SelectItem value="Laboratory">Laboratory</SelectItem>
                <SelectItem value="Progress Notes">Progress Notes</SelectItem>
                <SelectItem value="Discharge">Discharge</SelectItem>
              </SelectContent>
            </Select>

            {(filters.specialty || filters.status || filters.documentType) && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Search Results */}
      {(isLoading || results.length > 0) && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Search Results</span>
              {totalResults > 0 && (
                <Badge variant="secondary">
                  {totalResults} result{totalResults !== 1 ? "s" : ""}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="mb-2 h-4 w-3/4 rounded bg-gray-200"></div>
                    <div className="h-3 w-1/2 rounded bg-gray-200"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {results.map((result, index) => (
                  <div
                    key={`${result.type}-${result.id}-${index}`}
                    className="cursor-pointer rounded-lg border p-3 transition-colors hover:bg-gray-50"
                    onClick={() => handleResultClick(result)}
                  >
                    <div className="flex items-start space-x-3">
                      <Badge className={`mt-1 ${getResultColor(result.type)}`}>
                        {getResultIcon(result.type)}
                        <span className="ml-1 capitalize">{result.type}</span>
                      </Badge>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {result.highlight?.name ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHighlight(result.highlight.name.value),
                              }}
                            />
                          ) : (
                            result.title
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{result.subtitle}</div>
                        {(result.snippet?.content || result.snippet?.summary || result.highlight?.summary) && (
                          <div className="mt-1 text-sm text-gray-500">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHighlight(
                                  result.snippet?.content?.value ||
                                    result.snippet?.summary?.value ||
                                    result.highlight?.summary?.value
                                ),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
